import React from 'react'
import { useTranslation } from 'react-i18next'
import { Users, Shield, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'

export type WhoCanPostValue = 'anyone' | 'staff' | 'admins'

interface WhoCanPostSelectorProps {
  value: WhoCanPostValue
  onChange: (value: WhoCanPostValue) => void
  disabled?: boolean
}

export const WhoCanPostSelector: React.FC<WhoCanPostSelectorProps> = ({
  value,
  onChange,
  disabled = false
}) => {
  const { t } = useTranslation('common')

  const options = [
    {
      value: 'anyone' as const,
      icon: Users,
      label: t('modules.settings.whoCanPostAnyone'),
      hint: null
    },
    {
      value: 'staff' as const,
      icon: Shield,
      label: t('modules.settings.whoCanPostStaff'),
      hint: t('modules.settings.whoCanPostStaffHint')
    },
    {
      value: 'admins' as const,
      icon: Lock,
      label: t('modules.settings.whoCanPostAdmins'),
      hint: t('modules.settings.whoCanPostAdminsHint')
    }
  ]

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">
        {t('modules.settings.whoCanPost')}
      </label>
      <div className="flex flex-wrap gap-2">
        {options.map(({ value: optionValue, icon: Icon, label, hint }) => (
          <button
            key={optionValue}
            type="button"
            disabled={disabled}
            onClick={() => onChange(optionValue)}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm whitespace-nowrap",
              value === optionValue
                ? "bg-primary-100 text-primary-700"
                : "hover:bg-gray-100",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            title={hint || undefined}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            <span>{label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
